import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, fileUrl, money, apiError } from "@/lib/api";
import { Loader2, Lock, Truck } from "lucide-react";
import { toast } from "sonner";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function Checkout() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", phone: "", full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "" });
  const [rating, setRating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rates, setRates] = useState([]);
  const [checkoutId, setCheckoutId] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);

  // Any address/contact change invalidates previously fetched rates.
  const set = (k) => (e) => {
    setF({ ...f, [k]: e.target.value });
    if (rates.length) { setRates([]); setCheckoutId(null); setSelectedRate(null); }
  };

  if (items.length === 0) return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl text-kys-ink">Your bag is empty</h1>
      <Link to="/shop" className="mt-4 inline-flex text-sm font-semibold text-kys-accent">← Shop</Link>
    </div>
  );

  const addressReady = f.email && f.full_name && f.line1 && f.city && f.state && f.postal_code;

  const cartPayload = () => ({
    items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
    email: f.email, phone: f.phone,
    shipping_address: { full_name: f.full_name, line1: f.line1, line2: f.line2, city: f.city, state: f.state, postal_code: f.postal_code, country: "US" },
    hp: "",
  });

  const getRates = async () => {
    if (!addressReady) { toast.error("Please complete your contact and shipping address first."); return; }
    setRating(true);
    try {
      const { data } = await api.post("/shipping/rates", cartPayload());
      setRates(data.rates); setCheckoutId(data.checkout_id);
      setSelectedRate(data.rates[0]?.rate_id || null);
    } catch (err) { toast.error(apiError(err)); }
    finally { setRating(false); }
  };

  const pay = async () => {
    if (!checkoutId || !selectedRate) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        ...cartPayload(), origin_url: window.location.origin,
        shipping_checkout_id: checkoutId, shipping_rate_id: selectedRate,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  const shipCost = rates.find((r) => r.rate_id === selectedRate)?.amount;
  const total = subtotal + (shipCost ? Number(shipCost) : 0);
  const inputCls = "w-full rounded border border-kys-blush-border bg-white px-3 py-2.5 text-sm focus:border-kys-accent focus:outline-none";

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-serif text-4xl tracking-tight text-kys-ink">Checkout</h1>
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-md border border-kys-blush-border bg-white p-6">
            <h2 className="font-serif text-2xl text-kys-ink">Contact</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input data-testid="checkout-email" required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={inputCls} />
              <input data-testid="checkout-phone" placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={inputCls} />
            </div>
          </div>

          <div className="rounded-md border border-kys-blush-border bg-white p-6">
            <h2 className="font-serif text-2xl text-kys-ink">Shipping Address</h2>
            <p className="mt-1 text-xs text-kys-muted">United States addresses only.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input data-testid="checkout-name" required placeholder="Full name" value={f.full_name} onChange={set("full_name")} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="checkout-line1" required placeholder="Address line 1" value={f.line1} onChange={set("line1")} className={`${inputCls} sm:col-span-2`} />
              <input placeholder="Apt, suite (optional)" value={f.line2} onChange={set("line2")} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="checkout-city" required placeholder="City" value={f.city} onChange={set("city")} className={inputCls} />
              <select data-testid="checkout-state" required value={f.state} onChange={set("state")} className={inputCls}>
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input data-testid="checkout-zip" required placeholder="ZIP code" value={f.postal_code} onChange={set("postal_code")} className={inputCls} />
            </div>
          </div>

          <div className="rounded-md border border-kys-blush-border bg-white p-6">
            <div className="flex items-center gap-2"><Truck className="h-5 w-5 text-kys-accent" /><h2 className="font-serif text-2xl text-kys-ink">Shipping Method</h2></div>
            {rates.length === 0 ? (
              <>
                <p className="mt-2 text-sm text-kys-muted">Enter your address, then get live shipping rates for your order.</p>
                <button data-testid="get-rates-btn" onClick={getRates} disabled={!addressReady || rating}
                  className="mt-4 flex items-center justify-center gap-2 rounded-full bg-kys-ink px-6 py-3 text-sm font-semibold text-white transition hover:bg-kys-accent disabled:opacity-50">
                  {rating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculate Shipping"}
                </button>
              </>
            ) : (
              <div className="mt-4 space-y-2" data-testid="rate-options">
                {rates.map((r) => (
                  <label key={r.rate_id} data-testid={`rate-${r.rate_id}`}
                    className={`flex cursor-pointer items-center justify-between gap-3 rounded border p-3 text-sm transition ${selectedRate === r.rate_id ? "border-kys-accent bg-kys-blush" : "border-kys-blush-border hover:border-kys-accent"}`}>
                    <div className="flex items-center gap-3">
                      <input type="radio" name="rate" checked={selectedRate === r.rate_id} onChange={() => setSelectedRate(r.rate_id)} className="accent-kys-accent" />
                      <div>
                        <p className="font-medium text-kys-ink">{r.provider} {r.servicelevel}
                          {r.tag && <span className="ml-2 rounded-full bg-kys-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-kys-accent-dark">{r.tag}</span>}
                        </p>
                        {r.estimated_days != null && <p className="text-xs text-kys-muted">Est. {r.estimated_days} day{r.estimated_days === 1 ? "" : "s"}</p>}
                      </div>
                    </div>
                    <span className="font-mono text-kys-ink">{money(Number(r.amount))}</span>
                  </label>
                ))}
                <button onClick={getRates} disabled={rating} className="text-xs font-semibold text-kys-accent hover:underline">Recalculate</button>
              </div>
            )}
          </div>

          <div className="rounded-md border border-kys-blush-border bg-kys-blush p-4 text-sm text-kys-charcoal">
            <p className="flex items-center gap-2 font-medium"><Lock className="h-4 w-4" /> Secure checkout by Stripe</p>
            <p className="mt-1 text-kys-muted">You'll be redirected to Stripe's secure, encrypted checkout to complete your payment. U.S. shipping only.</p>
          </div>

          <button data-testid="place-order-btn" onClick={pay} disabled={busy || !selectedRate}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-kys-ink py-4 text-sm font-semibold text-white transition hover:bg-kys-accent disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (selectedRate ? "Continue to Payment" : "Select a shipping method")}
          </button>
        </div>

        <div>
          <div className="sticky top-20 rounded-md border border-kys-blush-border bg-white p-6">
            <h2 className="font-serif text-2xl text-kys-ink">Order</h2>
            <div className="mt-4 space-y-3">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.size}`} className="flex gap-3">
                  <div className="h-14 w-12 shrink-0 overflow-hidden rounded border border-kys-blush-border bg-kys-blush">
                    {it.image && <img src={fileUrl(it.image)} alt={it.name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex-1 text-xs">
                    <p className="font-medium text-kys-ink">{it.name}</p>
                    <p className="text-kys-muted">{it.size ? `${it.size} · ` : ""}Qty {it.quantity}</p>
                  </div>
                  <span className="font-mono text-xs">{money(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-kys-blush-border pt-4 text-sm">
              <div className="flex justify-between"><span className="text-kys-charcoal">Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-kys-charcoal">Shipping</span><span data-testid="summary-shipping" className="font-mono">{shipCost ? money(Number(shipCost)) : "—"}</span></div>
              <div className="mt-3 flex justify-between border-t border-kys-blush-border pt-3 text-base font-semibold text-kys-ink"><span>Total</span><span data-testid="summary-total" className="font-mono">{money(total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
